import { useState } from "react";
import { RegexValidatorResponse } from "../../models/regex/RegexValidator";
import { TransformRuleFactory } from "../../models/transformation/factory/TransformRuleFactory";
import {
  ReplaceAll,
  ReplaceAllData,
} from "../../models/transformation/ReplaceAll";
import {
  ReplaceFirst,
  ReplaceFirstData,
} from "../../models/transformation/ReplaceFirst";
import { getTransformRuleTypeDisplayValue } from "../../types/transformation/TransformationRuleTypes";
import { Button } from "../buttons/Button";
import Icon from "../Icon";
import { InputGroup } from "../inputGroup/InputGroup";
import { Label } from "../inputGroup/Label";
import { TransformTypeLabel } from "../inputGroup/TransformTypeLabel";
import { RegexError } from "../regex/RegexError";
import { RegexInput } from "../regex/RegexInput";
import { ReplaceTextWithSuggestions } from "./ReplaceTextWithSuggestions";
import { TransformRuleMatcherEditorChildProps } from "./TransformRuleEditor";
import "../../styles/transformation/replaceRuleEditor.css";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import {
  TTReplaceRuleLabel,
  TTTransformDeleteRule,
} from "../tooltips/ConfigurationTooltips";

export { ReplaceRuleEditor };

type Props =
  | TransformRuleMatcherEditorChildProps<ReplaceAll>
  | TransformRuleMatcherEditorChildProps<ReplaceFirst>;

const ReplaceRuleEditor = (props: Props) => {
  const rule = TransformRuleFactory.createRule(props.rule.toData()) as
    | ReplaceAll
    | ReplaceFirst;

  if (props.display === "view") {
    return <Display rule={rule} />;
  }
  return <Editor {...props} rule={rule} />;
};

type DisplayProps = {
  rule: ReplaceFirstData | ReplaceAllData;
};

const Display = (props: DisplayProps) => {
  const rule = props.rule;
  return (
    <div className={"transformRuleEditor replaceRuleEditor"}>
      <span>{rule.position}</span>
      {/* Find pattern */}
      <InputGroup className="findPatternIg">
        <Label>{getTransformRuleTypeDisplayValue(rule.discriminator)}</Label>
        <input
          type={"text"}
          className={"monospace"}
          value={rule.findPattern}
          disabled
        />
      </InputGroup>
      {/* With */}
      <InputGroup className="replaceTextIg">
        <Label>With</Label>
        <input type={"text"} value={rule.replaceText} disabled />
      </InputGroup>
    </div>
  );
};

type EditorProps = Omit<Props, "display" | "rule"> & {
  rule: ReplaceAll | ReplaceFirst;
};

const Editor = (props: EditorProps) => {
  const rule = props.rule;
  const [regexError, setRegexError] = useState<
    RegexValidatorResponse | undefined
  >();
  return (
    <div className={"transformRuleEditor replaceRuleEditor"}>
      <span>{rule.position}</span>

      {/* edit Find Pattern value */}
      <InputGroup button className="findPatternIg">
        <AdvancedTooltip tooltipText={TTReplaceRuleLabel}>
          {(tProps) => (
            <>
              {props.changedRuleType && (
                <TransformTypeLabel
                  discriminator={rule.getDiscriminator()}
                  onTypeChange={(type) =>
                    props.changedRuleType!(
                      TransformRuleFactory.changeRule(rule.toData(), type),
                    )
                  }
                >
                  {getTransformRuleTypeDisplayValue(rule.getDiscriminator())}
                </TransformTypeLabel>
              )}
              {!props.changedRuleType && (
                <Label>
                  {getTransformRuleTypeDisplayValue(rule.getDiscriminator())}
                </Label>
              )}
            </>
          )}
        </AdvancedTooltip>

        <RegexInput
          initialValue={rule.findPattern}
          updated={(result) => {
            setRegexError(result);
            if (result.valid) {
              rule.findPattern = result.regex;
              props.update(rule);
            }
          }}
        />
        <AdvancedTooltip overflow short tooltipText={TTTransformDeleteRule}>
          {(tProps) => (
            <Button
              {...tProps}
              onClick={() =>
                // delete
                props.update(null)
              }
            >
              <Icon altText={""} icon={"trash"} />
            </Button>
          )}
        </AdvancedTooltip>
      </InputGroup>

      <RegexError error={regexError} />

      {/* edit Replace Text value */}
      <ReplaceTextWithSuggestions
        fields={props.fields.filter((x) => !x.actualHidden).map((x) => x.fieldName)}
        replaceText={rule.replaceText}
        variables={props.variables}
        update={(x) => {
          rule.replaceText = x;
          props.update(rule);
        }}
      />
    </div>
  );
};
